import {
  ALL_STATUSES,
  CodingSummaryCard,
  DataStatusNote,
  deserify,
  EvidenceButton,
  formatAcademicYear,
  formatClassSection,
  formatDate,
  formatDuration,
  formatPercent,
  GalleryMedia,
  galleryItemsToMedia,
  GIL,
  GIL_CELL,
  GilColumn,
  GilReportTable,
  initialsOf,
  LearningOutcomesCard,
  matchesReportFilters,
  NoContentCard,
  OverviewSection,
  performanceLabel,
  projectLabel,
  ReportFilters,
  ReportLayout,
  SectionCard,
  StatTile,
  StatusChip,
  TimeSpentCard,
  toPercent,
  useCommonServiceClientContext,
  useGilReport,
} from '@geneo2-web/shared-ui';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CodeIcon from '@mui/icons-material/Code';
import { Box, Typography } from '@mui/material';
import {
  GilProjectStatusEnum,
  GilStudentProjectRow,
} from '@protos/learning_management/lms.assessment.student.apis_pb';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../reduxStore/reduxHooks';
import { Student_Gil_Project_Report } from '../../../routeHandling/RoutesNomenclature';

/** Encouragement under "Projects Completed", by how far along the student is. */
const progressCaption = (completed: number, assigned: number) => {
  if (!assigned) return 'No projects yet';
  if (completed === assigned) return 'All projects done!';
  if (completed * 2 >= assigned) return 'Great progress';
  return completed ? 'Keep going!' : "Let's get started";
};

/**
 * The work credited to the student on a project: their group's shared lab
 * device on a group project, their own sittings otherwise.
 */
const creditedTime = (row: GilStudentProjectRow) =>
  row.groupWork?.timeSpent ?? row.timeSpent;

/** Dashboard 2 - the student's own GIL journey across every assigned project. */
export const StudentGilJourney = () => {
  const navigate = useNavigate();
  const studentId = deserify(
    useAppSelector((state) => state.auth.userInfo?.studentProfileId)
  );
  const { LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);

  const { state, reload } = useGilReport(
    () =>
      studentId
        ? LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.getGilStudentJourneyReport(
            {}
          ).then((response) => response.data)
        : undefined,
    [studentId?.toString()]
  );

  const report = state.status === 'success' ? state.data : undefined;
  const summary = report?.summary;
  //~ Credited: includes the group's shared-device work on group projects.
  const totalTime = summary?.creditedTimeSpent ?? summary?.totalTimeSpent;
  const projects = useMemo(() => report?.projects ?? [], [report?.projects]);
  const assignedCount = summary?.projectsAssigned ?? 0;
  const completedCount = summary?.projectsCompleted ?? 0;
  const totalSec = totalTime?.totalTimeInSec ?? 0;
  const codingSec = totalTime?.codingTimeInSec ?? 0;
  //~ Each project's evidence sits on its own row, not in one pooled gallery.
  const evidenceByProject = useMemo(() => {
    const byProject = new Map<bigint, GalleryMedia[]>();
    (report?.gallery ?? []).forEach((item) => {
      byProject.set(item.assessmentId, [
        ...(byProject.get(item.assessmentId) ?? []),
        ...galleryItemsToMedia([item], false),
      ]);
    });
    return byProject;
  }, [report?.gallery]);

  const visibleProjects = useMemo(
    () =>
      projects.filter((row) =>
        matchesReportFilters(
          projectLabel(row.project),
          row.status,
          search,
          statusFilter
        )
      ),
    [projects, search, statusFilter]
  );

  const openProject = (row: GilStudentProjectRow) => {
    if (row.project?.assessmentId) {
      navigate(
        `${Student_Gil_Project_Report}?assessmentId=${row.project.assessmentId}`
      );
    }
  };

  const columns: GilColumn<GilStudentProjectRow>[] = [
    {
      key: 'index',
      header: '#',
      render: (_row, index) => (
        <Typography sx={GIL_CELL.index}>{index + 1}</Typography>
      ),
    },
    {
      key: 'project',
      header: 'Project',
      minWidth: 240,
      //~ The underlined name opens the student's report for that project.
      render: (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Typography
            role="link"
            tabIndex={0}
            onClick={() => openProject(row)}
            onKeyDown={(event) => event.key === 'Enter' && openProject(row)}
            sx={GIL_CELL.link}
          >
            {projectLabel(row.project)}
          </Typography>
          <Typography sx={GIL_CELL.sub}>
            Assigned {formatDate(row.project?.assignedDate)}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'group',
      header: 'Group Name',
      render: (row) => (
        <Typography noWrap sx={GIL_CELL.strong}>
          {row.groupName || '—'}
        </Typography>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusChip status={row.status} />,
    },
    {
      key: 'time',
      header: 'Time Spent',
      render: (row) =>
        row.status === GilProjectStatusEnum.GIL_PROJECT_STATUS_NOT_STARTED ? (
          <Typography noWrap sx={GIL_CELL.sub}>
            Not Started
          </Typography>
        ) : (
          formatDuration(creditedTime(row)?.totalTimeInSec)
        ),
    },
    {
      key: 'evidence',
      header: 'My Work',
      render: (row) => (
        <EvidenceButton
          items={
            evidenceByProject.get(row.project?.assessmentId ?? BigInt(0)) ?? []
          }
          title={projectLabel(row.project)}
        />
      ),
    },
  ];

  return (
    <ReportLayout
      title="My GIL Journey"
      avatarText={initialsOf(report?.studentName) || undefined}
      avatarVariant="circle"
      meta={
        report && [
          {
            value: report.studentName || 'Student',
            iconName: 'user',
            color: '#EEF0FF',
            textColor: '#2E3192',
            bold: true,
          },
          {
            label: 'Class',
            value: formatClassSection(report.classSection),
            color: '#FFF8E6',
            textColor: GIL.text,
            bold: true,
          },
          {
            label: 'Academic Year',
            value: formatAcademicYear(report.classSection?.academicYear),
          },
        ]
      }
      state={state}
      onRetry={reload}
    >
      <DataStatusNote status={report?.dataStatus} />

      <OverviewSection>
        <StatTile
          tone="green"
          icon={<CheckCircleOutlineIcon />}
          value={`${completedCount}/${assignedCount}`}
          label="Projects Completed"
          caption={progressCaption(completedCount, assignedCount)}
        />
        <StatTile
          tone="blue"
          icon={<AccessTimeIcon />}
          value={formatDuration(totalSec)}
          label="Total Time Spent"
          caption="Across all activities"
        />
        <StatTile
          tone="purple"
          icon={<CodeIcon />}
          value={formatDuration(codingSec)}
          label="Coding Time"
          caption={
            totalSec
              ? `${Math.round(
                  toPercent(codingSec, totalSec)
                )}% of your total time`
              : 'No coding yet'
          }
        />
        <StatTile
          tone="red"
          icon="gil-star-red"
          value={formatPercent(summary?.avgProjectScorePercent)}
          label="Avg. Project Score"
          caption={performanceLabel(summary?.avgProjectScorePercent)}
        />
      </OverviewSection>

      <TimeSpentCard
        videoSec={totalTime?.videoActivityBookTimeInSec}
        codingSec={codingSec}
        subtitle="Group projects count your group's time together"
        effortText="You are putting consistent effort into your learning journey."
      />

      {/* Credited: one entry per project coded in (with the group on group
          projects), so the averages read as per-project means. */}
      <CodingSummaryCard
        summary={summary?.creditedCodingSummary ?? summary?.codingSummary}
        title="My Coding Journey"
        subtitle="Across every project you have coded in"
      />

      <SectionCard
        title="My Projects"
        subtitle="Track your progress and performance across all assigned projects"
        action={
          projects.length > 0 && (
            <ReportFilters
              search={search}
              onSearchChange={setSearch}
              status={statusFilter}
              onStatusChange={setStatusFilter}
            />
          )
        }
      >
        {projects.length === 0 ? (
          <NoContentCard
            variant="info"
            icon="laboratory"
            text="No GIL projects assigned yet"
          />
        ) : (
          <GilReportTable
            columns={columns}
            rows={visibleProjects}
            rowKey={(row) => String(row.project?.assessmentId ?? 0)}
            emptyText="No projects match your search"
          />
        )}
      </SectionCard>

      <LearningOutcomesCard
        outcomes={report?.learningOutcomes}
        showProjects
        title="My Learning Outcomes"
        subtitle="How you are doing on each learning outcome across your projects"
      />
    </ReportLayout>
  );
};

export default StudentGilJourney;
